import { useState, useEffect, useCallback } from 'react';
import { Post, Source } from '@sodamnfoolish/sjc-backend-types/src/api/feed/dto';
import { SoundStatus } from '../../post-interface';
import { AdvancedFeedHeader } from '../posts/AdvancedFeedHeader';
import InteractionBanner from '../Banner';
import { TimerProvder } from '../TimeProvider';
import { usePumpFunBubbles } from '../../Hooks/usePumpFunBubbles';
import { useFeed } from '../../Hooks/posts/useFeed';
import { PostTweetTypes } from '../../Hooks/posts/types';
import { playSound } from '../../Hooks/posts/sound';
import { Posts } from '../posts/Posts';
import newGroupBubble from '../../utils/audio/newGroupBubble.wav';
import PumpFunsIcon from '../assets/PumpFunsIcon.svg';
import BonkIcon from '../assets/bonk_fun.png';

import '../Styles/MonitorFeed.css';
import { useBonkBubbles } from '../../Hooks/useBonkBubbles';
import { Bubbles } from '../bubbles/Bubbles';
import { createPumpFunTokensSorter, createBonkTokensSorter } from '../../utils/bubbles/sort';
import * as pumpfunCalcs from '../../utils/pumpfun/calc';
import * as bonkCalcs from '../../utils/bonk/calc';
import { LiveDataBonkTokensProvider } from '../LiveDataBonkTokensProvider';
import { LiveDataPumpTokensProvider } from '../LiveDataPumpTokensProvider';
import { SolPriceProvder } from '../SolPriceProvider';

const EveryoneSource = { username: 'Everyone' } as Source;

const newGroupBubbleSound = new Audio(newGroupBubble);

export function MonitorFeed({
    sources,
    isGeneralPostsVisible,
    isPumpFunsPostsVisible,
    isBonkPostsVisible,
    tweetTypes,
    updateTweetTypes,
    sourceOrder,
}: {
    sources: Source[];
    isGeneralPostsVisible: boolean;
    isPumpFunsPostsVisible: boolean;
    isBonkPostsVisible: boolean;
    tweetTypes: PostTweetTypes;
    updateTweetTypes: (username: string, newSelectedTweetTypes: Set<string>, newSoundTweetTypes: Set<string>) => void;
    sourceOrder: string[];
}) {
    const [soundsEnabled, setSoundsEnabled] = useState<SoundStatus>(() => {
        const savedSoundStatus = localStorage.getItem('soundsEnabled');
        if (savedSoundStatus) return JSON.parse(savedSoundStatus);

        const defaultSoundStatus: SoundStatus = {};
        sources.forEach((source) => {
            defaultSoundStatus[source.username] = true;
        });
        return defaultSoundStatus;
    });
    const [highlightedPosts, setHighlightedPosts] = useState<Set<string>>(new Set());
    const orderedSources = sourceOrder.map((username) => sources.find((source) => source.username === username)).filter(Boolean) as Source[];

    useEffect(() => {
        setSoundsEnabled((prev) => {
            const updated = { ...prev };
            let changed = false;
            sources.forEach((source) => {
                if (updated[source.username] === undefined) {
                    updated[source.username] = true;
                    changed = true;
                }
            });
            return changed ? updated : prev;
        });
    }, [sources]);

    useEffect(() => {
        localStorage.setItem('soundsEnabled', JSON.stringify(soundsEnabled));
    }, [soundsEnabled]);

    const feed = useFeed({
        sources,
        onNewPost: (post) => {
            if (soundsEnabled[post.username]) {
                playSound(post, tweetTypes);
            }
            setHighlightedPosts((prev) => {
                const updated = new Set(prev);
                updated.add(post.id);
                return updated;
            });
            setTimeout(() => {
                setHighlightedPosts((prev) => {
                    const updated = new Set(prev);
                    updated.delete(post.id);
                    return updated;
                });
            }, 60000);
        },
    });

    const [pumpFunBubbles, removePumpBubble] = usePumpFunBubbles({
        enabled: isPumpFunsPostsVisible,
        onNewBubble: () => {
            if (isPumpfunSoundEnabled()) {
                newGroupBubbleSound.play().catch((e) => console.warn('Sound play failed:', e));
            }
        },
    });

    const [bonkBubbles, removeBonkBubble] = useBonkBubbles({
        enabled: isBonkPostsVisible,
        onNewBubble: () => {
            if (isBonkSoundEnabled()) {
                newGroupBubbleSound.play().catch((e) => console.warn('Sound play failed:', e));
            }
        },
    });

    const bubbles = pumpFunBubbles.bubbles;
    const shouldLinkPost = useCallback((post: Post) => Boolean(bubbles.find((b) => post.pumpFunBubble?.id === b.id)), [bubbles]);
    const shouldHighlightPost = useCallback((post: Post) => highlightedPosts.has(post.id), [highlightedPosts]);

    return (
        <section>
            <InteractionBanner />
            <div className='body'>
                {isGeneralPostsVisible && (
                    <Posts
                        key='Everyone'
                        withUsernames
                        posts={feed.Everyone}
                        source={EveryoneSource}
                        shouldHighlight={shouldHighlightPost}
                        shouldLink={shouldLinkPost}
                    />
                )}

                {isBonkPostsVisible && (
                    <LiveDataBonkTokensProvider>
                        <TimerProvder>
                            <SolPriceProvder>
                                <Bubbles
                                    icon={BonkIcon}
                                    bubbleType='bonk'
                                    bubbleName='LetsBonk'
                                    bubbles={bonkBubbles.bubbles}
                                    sources={sources}
                                    loading={bonkBubbles.loading}
                                    defaultSoundState={isBonkSoundEnabled()}
                                    sort={[
                                        {
                                            key: 'creationTime',
                                            value: 'Creation time',
                                            ASC: createBonkTokensSorter('deployBlockTime', 'ASC', Number),
                                            DESC: createBonkTokensSorter('deployBlockTime', 'DESC', Number),
                                        },
                                    ]}
                                    getBondingCurveProgress={bonkCalcs.getBondingCurveProgress}
                                    calculateMarketCap={bonkCalcs.calculateMarketCap}
                                    onBubbleRemove={removeBonkBubble}
                                />
                            </SolPriceProvder>
                        </TimerProvder>
                    </LiveDataBonkTokensProvider>
                )}

                {isPumpFunsPostsVisible && (
                    <LiveDataPumpTokensProvider>
                        <TimerProvder>
                            <SolPriceProvder>
                                <Bubbles
                                    icon={PumpFunsIcon}
                                    bubbleType='pumpfun'
                                    bubbleName='PumpFuns'
                                    bubbles={pumpFunBubbles.bubbles}
                                    sources={sources}
                                    loading={pumpFunBubbles.loading}
                                    defaultSoundState={isPumpfunSoundEnabled()}
                                    sort={[
                                        {
                                            key: 'bonding',
                                            value: 'Bonding %',
                                            ASC: createPumpFunTokensSorter('virtualTokenReserves', 'ASC', pumpfunCalcs.calculateBondingCurveProgress),
                                            DESC: createPumpFunTokensSorter('virtualTokenReserves', 'DESC', pumpfunCalcs.calculateBondingCurveProgress),
                                        },
                                        {
                                            key: 'creationTime',
                                            value: 'Creation time',
                                            ASC: createPumpFunTokensSorter('deployBlockTime', 'ASC', Number),
                                            DESC: createPumpFunTokensSorter('deployBlockTime', 'DESC', Number),
                                        },
                                    ]}
                                    getBondingCurveProgress={pumpfunCalcs.getBondingCurveProgress}
                                    calculateMarketCap={pumpfunCalcs.calculateMarketCap}
                                    onBubbleRemove={removePumpBubble}
                                />
                            </SolPriceProvder>
                        </TimerProvder>
                    </LiveDataPumpTokensProvider>
                )}

                {orderedSources.map((source) => {
                    const sourceTweetTypes = tweetTypes[source.username];
                    return (
                        <div key={source.username}>
                            {sourceTweetTypes ? (
                                <Posts
                                    posts={feed[source.username]}
                                    source={source}
                                    shouldHighlight={shouldHighlightPost}
                                    shouldLink={shouldLinkPost}
                                    renderSource={(source, sourceDetails) => (
                                        <AdvancedFeedHeader
                                            source={source}
                                            {...sourceDetails}
                                            soundStatus={soundsEnabled}
                                            soundTweetTypes={sourceTweetTypes.soundTweetTypes}
                                            selectedTweetTypes={sourceTweetTypes.selectedTweetTypes}
                                            onSoundStatusChange={setSoundsEnabled}
                                            onTweetTypesUpdate={updateTweetTypes}
                                        />
                                    )}
                                />
                            ) : (
                                <div>Loading tweet types...</div>
                            )}
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

function isPumpfunSoundEnabled(): boolean {
    return localStorage.getItem('pumpSoundEnabled') !== 'false';
}

function isBonkSoundEnabled(): boolean {
    return localStorage.getItem('bonkSoundEnabled') !== 'false';
}
